import { useState } from 'react';
import { useAllEngagementsQuery } from '@anytag/graphql';
import { NOT_FOUND } from '@anytag/shared-constants';
import { Header, ListWithPagination } from '@anytag/shared-molecules';
import { useEngagementPermissions, useUserRoles } from '@anytag/auth';
import { getOffset, getValuableVariables } from '@anytag/shared-utils';
import { ButtonLink, RenderDataWithFallback } from '@anytag/shared-atoms';
import { useQueryHelper, useUpdateDocumentTitle } from '@anytag/shared-hooks';
import {
  EngagementOnboarding,
  EngagementCampaignsList,
  EngagementCampaignsFilter,
  useEngagementCampaignsFilter
} from '@anytag/shared-organisms';

export const Engagements = () => {
  useUpdateDocumentTitle({ title: 'documentTitle.Engagement', appHeader: 'appHeader.Engagement' });
  const { isAdvertiser } = useUserRoles();
  const { t, enqueueSnackbar } = useQueryHelper();
  const [isOnboarding, setIsOnboarding] = useState(false);
  const { hideAddEngagementBtn } = useEngagementPermissions();
  const { page, limit, filter } = useEngagementCampaignsFilter();

  const { data, previousData, loading } = useAllEngagementsQuery({
    fetchPolicy: 'no-cache', // will always trigger onError to setIsOnboarding
    variables: getValuableVariables({
      limit,
      offset: getOffset(page, limit),
      ...filter,
      status: filter.status || null,
      salesPicIds: filter.salesPicIds.map(Number),
      influencerPicIds: filter.influencerPicIds.map(Number),
      campaignCategoryIds: filter.campaignCategoryIds.map(Number)
    }),
    onError: (err) => {
      if (err.message === NOT_FOUND && isAdvertiser) {
        setIsOnboarding(true);
      } else {
        enqueueSnackbar(t(err.message), { variant: 'error' });
      }
    }
  });

  const result = loading ? previousData : data;
  const totalRecords = result?.allEngagementsCount?.totalNumber;

  return (
    <RenderDataWithFallback
      hasNoData={isOnboarding}
      loading={isAdvertiser && loading}
      noDataNode={<EngagementOnboarding />}
    >
      <ListWithPagination pagination={{ totalRecords }}>
        <Header>
          {!hideAddEngagementBtn ? (
            <ButtonLink
              variant="solid"
              to="/engagement/add"
              title={t('Button.Add Campaign')}
              className="ml-auto min-w-36 capitalize"
              prefixIcon={{ icon: 'plus', size: '10px' }}
            />
          ) : null}
        </Header>
        <EngagementCampaignsFilter />
        <EngagementCampaignsList loading={loading} totalRecords={totalRecords} data={result?.allEngagements || []} />
      </ListWithPagination>
    </RenderDataWithFallback>
  );
};
